import React, { useState, useEffect } from 'react';
import { BottomNav, Spinner } from './ui';
import { C, BADGES } from '../data/constants';
import { fetchLeaderboard, countProfiles } from '../lib/db';
import { isConfigured } from '../lib/supabase';

const TABS = ['All Time', 'This Week', 'Today'];
const MEDALS = ['🥇', '🥈', '🥉'];

// Mock data shown only when Supabase is not configured
const MOCK_TOP3 = [
  { username: 'Amara K.',  total_score: 2840, earned_badges: [0,1,2,3] },
  { username: 'Kwame A.',  total_score: 3520, earned_badges: [0,1,2,3,5,6] },
  { username: 'Nia B.',    total_score: 2610, earned_badges: [0,1,3] },
];
const MOCK_LIST = [
  { username: 'Seun O.',   earned_badges: [0,1,6],   total_score: 2480 },
  { username: 'Zanele M.', earned_badges: [0,1,2],   total_score: 2210 },
  { username: 'Kofi N.',   earned_badges: [0,1],     total_score: 1990 },
  { username: 'Ade T.',    earned_badges: [0,1],     total_score: 1810 },
  { username: 'Zara M.',   earned_badges: [0,1,3],   total_score: 1650 },
  { username: 'Amara N.',  earned_badges: [0],        total_score: 1420 },
  { username: 'Temi O.',   earned_badges: [0,1,5],   total_score: 1280 },
  { username: 'Chidi K.',  earned_badges: [0,1,2,4], total_score: 980  },
  { username: 'Adaeze C.', earned_badges: [0,2],     total_score: 820  },
];

function getBadgeTitle(earnedBadges) {
  if (!earnedBadges?.length) return "Seeker of Ma'at";
  const top = Math.max(...earnedBadges);
  return BADGES[top]?.name || "Seeker of Ma'at";
}

function getPlayerIcon(earnedBadges) {
  if (!earnedBadges?.length) return '𓂀';
  const top = Math.max(...earnedBadges);
  return BADGES[top]?.ic || '𓂀';
}

export default function Leaderboard({ onTab, activeTab, player, user }) {
  const [activeTime, setActiveTime] = useState('All Time');
  const [lbData,     setLbData]     = useState(null);   // null = loading
  const [totalCount, setTotal]      = useState(null);
  const [error,      setError]      = useState(false);

  useEffect(() => {
    if (!isConfigured) { setLbData([]); return; }
    fetchLeaderboard(50)
      .then(data => setLbData(data || []))
      .catch(() => setError(true));
    countProfiles()
      .then(c => setTotal(c))
      .catch(() => {});
  }, []);

  const loading = isConfigured && lbData === null && !error;
  const useReal = isConfigured && lbData !== null && !error;

  // Build the data to display
  const rawList = useReal ? lbData : MOCK_LIST;

  // Inject the current player into the list if they have a score
  const playerEntry = player?.totalScore > 0 ? {
    username:      player.username || 'You',
    total_score:   player.totalScore,
    earned_badges: player.earnedBadges || [],
    _isMe:         true,
  } : null;

  let fullList = [...rawList];
  if (playerEntry) {
    const alreadyIn = useReal && lbData.some(r =>
      user && r.username === player.username
    );
    if (!alreadyIn) fullList.push(playerEntry);
  }
  fullList.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

  const top3   = (useReal ? lbData : MOCK_TOP3).slice(0, 3);
  const rest   = fullList.slice(3);

  // Podium order: 2nd (index 1), 1st (index 0), 3rd (index 2)
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);
  const podiumRanks = [2, 1, 3];

  const displayCount = totalCount ?? (useReal ? (lbData.length + 500) : 1203) + (player?.gamesPlayed || 0);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: '#fff', padding: '14px 16px', borderBottom: `2px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 2 }}>Hall of Fame 🏛️</div>
        <div style={{ fontSize: 22, fontWeight: 900 }}>Top Scholars</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {loading ? '…' : `${displayCount.toLocaleString()} players competing`}
          {!isConfigured && <span style={{ color: C.faint }}> — configure Supabase for live rankings</span>}
        </div>
      </div>

      {/* Time tabs */}
      <div style={{ display: 'flex', padding: '8px 16px 6px', gap: 5, borderBottom: `2px solid ${C.border}`, flexShrink: 0, background: '#fff' }}>
        {TABS.map(t => {
          const active = activeTime === t;
          return (
            <div key={t} onClick={() => setActiveTime(t)}
              style={{ flex: 1, padding: 8, textAlign: 'center', fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: 10, background: active ? C.amber : '#F5F0F0', color: active ? '#fff' : C.muted, boxShadow: active ? '0 2px 0 #C07000' : 'none', border: active ? 'none' : `2px solid ${C.border}` }}>
              {t}
            </div>
          );
        })}
      </div>

      <div className="scroll-area">
        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: C.muted, fontSize: 13 }}>
            <Spinner /> Loading rankings…
          </div>
        )}

        {!loading && (activeTime !== 'All Time') && (
          <div style={{ background: '#F8F4F0', borderRadius: 12, margin: '12px 16px', padding: '10px 12px', fontSize: 12, color: C.muted, textAlign: 'center' }}>
            Period leaderboards coming soon! Showing all-time rankings.
          </div>
        )}

        {!loading && (
          <>
            {/* Podium */}
            {podiumOrder.length > 0 && (
              <div style={{ background: 'linear-gradient(180deg,#FFF9E8,#FFFAF2)', padding: '14px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr 1fr', gap: 6, alignItems: 'flex-end' }}>
                  {podiumOrder.map((p, vi) => {
                    const rank  = podiumRanks[vi];
                    const big   = rank === 1;
                    const col   = rank === 1 ? C.amber : rank === 2 ? '#888' : '#C08040';
                    const bc    = rank === 1 ? C.amber : rank === 2 ? '#CCC' : '#C09050';
                    const heights = [28, 44, 18];
                    const h     = heights[podiumRanks.indexOf(rank)];
                    const icon  = getPlayerIcon(p.earned_badges);
                    return (
                      <div key={rank} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: col, marginBottom: 3 }}>{MEDALS[rank - 1]} #{rank}</div>
                        <div style={{ width: big ? 60 : 48, height: big ? 60 : 48, borderRadius: '50%', background: big ? '#FFF9E8' : '#fff', border: `${big ? 3 : 2}px solid ${bc}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: big ? 26 : 20, margin: '0 auto 5px', boxShadow: big ? '0 4px 12px rgba(232,160,32,.3)' : 'none' }}>
                          {icon}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 1 }}>{p.username}</div>
                        <div style={{ fontSize: big ? 18 : 14, fontWeight: 900, color: col }}>{(p.total_score || 0).toLocaleString()}</div>
                        <div style={{ height: h, background: rank === 1 ? 'linear-gradient(180deg,#FFD080,#FFE8A0)' : rank === 2 ? '#E0E0E0' : '#D0A060', borderRadius: '5px 5px 0 0', marginTop: 5 }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* List */}
            <div style={{ padding: '4px 16px 12px' }}>
              {rest.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: C.muted, fontSize: 13 }}>
                  {useReal ? '🌍 No scores yet — be the first to play!' : 'No scores for this period.'}
                </div>
              )}
              {rest.map((r, i) => {
                const rank    = i + 4;
                const isMe    = r._isMe;
                const icon    = getPlayerIcon(r.earned_badges);
                const badge   = getBadgeTitle(r.earned_badges);
                return (
                  <div key={i}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: `2px solid ${isMe ? C.goldLight : C.border}`, background: isMe ? '#FFFBF0' : 'transparent', margin: isMe ? '0 -16px' : 0, padding: isMe ? '10px 16px' : '10px 0' }}>
                    <div style={{ width: 22, fontSize: 12, fontWeight: 700, color: isMe ? C.amber : C.muted, textAlign: 'center', flexShrink: 0 }}>{rank}</div>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', border: `2px solid ${isMe ? C.amber : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isMe ? C.amber : C.text }}>
                        {r.username}{isMe ? ' (You)' : ''}
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 1, fontWeight: 500 }}>{badge}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 900, color: isMe ? C.amber : C.text }}>
                      {(r.total_score || 0).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <BottomNav active={activeTab} onTab={onTab} />
    </div>
  );
}
